<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline */
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import IxcInvoiceNotificationDeliveriesAPI from 'dashboard/api/ixcInvoiceNotificationDeliveries';

const router = useRouter();
const isLoading = ref(false);
const enabled = ref(true);
const rows = ref([]);
const summary = ref({ total: 0, sent: 0, failed: 0, pending: 0, skipped: 0 });
const hasMore = ref(false);
const lastUpdatedAt = ref(null);
const configuration = ref({});
const filters = reactive({ status: '', notification_type: '' });
const today = new Date();
const thirtyDaysAgo = new Date(today);
thirtyDaysAgo.setDate(today.getDate() - 29);
const defaultDueDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
const dateRange = ref([thirtyDaysAgo, today]);
const rangeType = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const detailsDialogRef = ref(null);
const testDialogRef = ref(null);
const csvDialogRef = ref(null);
const selectedGroup = ref(null);
const isTesting = ref(false);
const isImporting = ref(false);
const csvMode = ref('paste');
const csvFile = ref(null);
const importResult = ref(null);
const testForm = reactive({
  document: '',
  phone: '',
  notification_type: 'before_due',
});
const csvContent = ref('');
const csvForm = reactive({
  inbox_id: '',
  template_key: '',
  due_date: defaultDueDate,
});

const selectClass =
  'block h-10 w-full reset-base !mb-0 appearance-none rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 pr-10 text-sm leading-6 text-n-slate-12 outline outline-1 outline-n-slate-5 outline-offset-[-1px] transition select-caret focus:outline-n-brand disabled:cursor-not-allowed disabled:opacity-60 dark:outline-n-weak';
const typeOptions = [
  {
    value: 'before_due',
    label: 'Boleto a vencer',
    icon: 'i-lucide-calendar-clock',
  },
  {
    value: 'after_due',
    label: 'Boleto vencido',
    icon: 'i-lucide-circle-alert',
  },
];
const csvModeOptions = [
  { value: 'paste', label: 'Colar conteúdo', icon: 'i-lucide-clipboard-list' },
  { value: 'file', label: 'Enviar arquivo', icon: 'i-lucide-file-up' },
];
const statusOptions = [
  { value: '', label: 'Todos' },
  { value: 'sent', label: 'Aceitos pelo WhatsApp' },
  { value: 'failed', label: 'Falhas' },
  { value: 'skipped', label: 'Ignorados' },
  { value: 'pending', label: 'Pendentes' },
];
const statusLabels = {
  pending: 'Pendente',
  sent: 'Aceito',
  failed: 'Falha',
  skipped: 'Ignorado',
};
const statusClasses = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  sent: 'bg-n-teal-3 text-n-teal-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
  skipped: 'bg-n-slate-3 text-n-slate-11',
};
const reportTypeOptions = [{ value: '', label: 'Todos' }, ...typeOptions];

const metricCards = computed(() => [
  {
    key: 'total',
    label: 'Tentativas',
    value: summary.value.total || 0,
    icon: 'i-lucide-layers-3',
    iconClass: 'bg-n-slate-3 text-n-slate-11',
  },
  {
    key: 'sent',
    label: 'Aceitos',
    value: summary.value.sent || 0,
    icon: 'i-lucide-message-circle-check',
    iconClass: 'bg-n-teal-3 text-n-teal-11',
  },
  {
    key: 'failed',
    label: 'Falhas',
    value: summary.value.failed || 0,
    icon: 'i-lucide-circle-x',
    iconClass: 'bg-n-ruby-3 text-n-ruby-11',
  },
  {
    key: 'pending',
    label: 'Pendentes',
    value: summary.value.pending || 0,
    icon: 'i-lucide-clock-3',
    iconClass: 'bg-n-amber-3 text-n-amber-11',
  },
  {
    key: 'skipped',
    label: 'Ignorados',
    value: summary.value.skipped || 0,
    icon: 'i-lucide-skip-forward',
    iconClass: 'bg-n-slate-3 text-n-slate-11',
  },
]);
const acceptedRate = computed(() => {
  if (!summary.value.total) return '0,0%';
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(summary.value.sent / summary.value.total);
});

const formatDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR').format(
    new Date(`${value}`.slice(0, 10) + 'T12:00:00')
  );
};
const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};
const dateParam = value => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatCurrency = value =>
  value === null || value === undefined || value === ''
    ? '-'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value));
const typeLabel = type =>
  typeOptions.find(item => item.value === type)?.label || type;
const deliveryDate = row => row.sent_at || row.scheduled_at || row.created_at;
const deliveryMinute = row => {
  const date = new Date(deliveryDate(row));
  return Number.isNaN(date.getTime())
    ? deliveryDate(row)
    : Math.floor(date.getTime() / 60000);
};
const statusCount = (items, status) =>
  items.filter(item => item.status === status).length;
const groupSummary = items => ({
  total: items.length,
  sent: statusCount(items, 'sent'),
  failed: statusCount(items, 'failed'),
  pending: statusCount(items, 'pending'),
  skipped: statusCount(items, 'skipped'),
});
const groupErrors = items => [
  ...new Set(items.map(item => item.error_message).filter(Boolean)),
];
const groupedReportRows = computed(() => {
  const groups = new Map();
  rows.value.forEach(row => {
    const sentDate = formatDateTime(deliveryDate(row));
    const key = `${deliveryMinute(row)}-${row.notification_type}`;
    if (!groups.has(key))
      groups.set(key, {
        key,
        sentDate,
        notificationType: row.notification_type,
        rows: [],
      });
    groups.get(key).rows.push(row);
  });
  return Array.from(groups.values()).map(group => ({
    ...group,
    summary: groupSummary(group.rows),
    errors: groupErrors(group.rows),
  }));
});
const selectedGroupSummary = computed(() =>
  selectedGroup.value ? groupSummary(selectedGroup.value.rows) : summary.value
);
const selectedTestConfiguration = computed(
  () => configuration.value[testForm.notification_type] || {}
);
const manualInboxes = computed(
  () => configuration.value?.manual_options?.inboxes || []
);
const selectedCsvInbox = computed(() =>
  manualInboxes.value.find(
    inbox => String(inbox.id) === String(csvForm.inbox_id)
  )
);
const csvTemplates = computed(() => selectedCsvInbox.value?.templates || []);
const templateKey = template => `${template.name}::${template.language}`;
const selectedCsvTemplate = computed(() =>
  csvTemplates.value.find(
    template => templateKey(template) === csvForm.template_key
  )
);
const digitsOnly = (value, limit) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, limit);
const formatCpf = value =>
  digitsOnly(value, 11)
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
const formatPhone = value => {
  const digits = digitsOnly(value, 11);
  if (!digits) return '';
  if (digits.length < 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  const subscriberLength = digits.length === 11 ? 5 : 4;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 2 + subscriberLength)}-${digits.slice(2 + subscriberLength)}`;
};
const testDocument = computed({
  get: () => formatCpf(testForm.document),
  set: value => {
    testForm.document = digitsOnly(value, 11);
  },
});
const testPhone = computed({
  get: () => formatPhone(testForm.phone),
  set: value => {
    testForm.phone = digitsOnly(value, 11);
  },
});
const conversationHref = row => {
  if (!row.conversation_id) return '';
  return router.resolve({
    name: 'inbox_conversation',
    params: {
      ...router.currentRoute.value.params,
      conversation_id: row.conversation_id,
    },
  }).href;
};

function ensureCsvConfiguration() {
  if (
    !manualInboxes.value.some(
      inbox => String(inbox.id) === String(csvForm.inbox_id)
    )
  ) {
    csvForm.inbox_id = manualInboxes.value[0]?.id || '';
  }
  if (
    !csvTemplates.value.some(
      template => templateKey(template) === csvForm.template_key
    )
  ) {
    csvForm.template_key = csvTemplates.value[0]
      ? templateKey(csvTemplates.value[0])
      : '';
  }
}

async function fetchReport() {
  isLoading.value = true;
  try {
    const { data } = await IxcInvoiceNotificationDeliveriesAPI.get({
      ...filters,
      sent_since: dateParam(dateRange.value[0]),
      sent_until: dateParam(dateRange.value[1]),
      limit: 2000,
    });
    enabled.value = data?.enabled !== false;
    rows.value = data?.payload || [];
    summary.value = data?.summary || summary.value;
    hasMore.value = data?.has_more === true;
    lastUpdatedAt.value = new Date();
    configuration.value = data?.configuration || {};
    ensureCsvConfiguration();
  } catch (error) {
    enabled.value = error?.response?.status !== 403;
    useAlert(
      enabled.value
        ? 'Não foi possível carregar o relatório IXC.'
        : 'A integração IXC precisa estar ativa.'
    );
  } finally {
    isLoading.value = false;
  }
}

const onDateRangeChanged = ([startDate, endDate, selectedRange]) => {
  if (!startDate || !endDate) return;
  dateRange.value = [startDate, endDate];
  rangeType.value = selectedRange;
  fetchReport();
};
const openTest = () => testDialogRef.value?.open();
const openCsv = () => {
  importResult.value = null;
  ensureCsvConfiguration();
  csvDialogRef.value?.open();
};
const openDetails = group => {
  selectedGroup.value = group;
  detailsDialogRef.value?.open();
};
const apiError = error =>
  error?.response?.data?.error || 'Não foi possível concluir a operação.';

async function submitTest() {
  isTesting.value = true;
  try {
    await IxcInvoiceNotificationDeliveriesAPI.testDelivery(testForm);
    testDialogRef.value?.close();
    useAlert(
      'Teste agendado. O boleto real foi localizado no IXC e enviado ao telefone informado.'
    );
    fetchReport();
  } catch (error) {
    useAlert(apiError(error));
  } finally {
    isTesting.value = false;
  }
}

const onCsvFile = event => {
  csvFile.value = event.target.files?.[0] || null;
};
const onCsvInboxChange = () => {
  csvForm.template_key = '';
  ensureCsvConfiguration();
};
async function submitCsv() {
  isImporting.value = true;
  importResult.value = null;
  try {
    if (!csvForm.inbox_id || !selectedCsvTemplate.value || !csvForm.due_date) {
      useAlert('Selecione a caixa, o template e a data de vencimento.');
      return;
    }
    const importConfiguration = {
      inbox_id: csvForm.inbox_id,
      template_name: selectedCsvTemplate.value.name,
      template_language: selectedCsvTemplate.value.language,
      due_date: csvForm.due_date,
    };
    let payload;
    if (csvMode.value === 'file' && csvFile.value) {
      payload = new FormData();
      payload.append('file', csvFile.value);
      Object.entries(importConfiguration).forEach(([key, value]) =>
        payload.append(key, value)
      );
    } else if (csvMode.value === 'paste') {
      payload = { csv_content: csvContent.value, ...importConfiguration };
    } else {
      useAlert('Selecione um arquivo CSV.');
      return;
    }
    const { data } =
      await IxcInvoiceNotificationDeliveriesAPI.importCsv(payload);
    importResult.value = data;
    useAlert(`${data.scheduled} aviso(s) agendado(s).`);
    fetchReport();
  } catch (error) {
    useAlert(apiError(error));
  } finally {
    isImporting.value = false;
  }
}

onMounted(fetchReport);
</script>

<template>
  <div class="mx-auto w-full max-w-[1440px] space-y-5 pb-8 pt-5">
    <section
      class="flex flex-col gap-5 rounded-2xl border border-n-weak bg-n-card p-5 shadow-sm sm:p-6 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="flex min-w-0 items-start gap-4">
        <span
          class="grid size-12 shrink-0 place-content-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <i class="i-lucide-receipt-text size-6" />
        </span>
        <div class="min-w-0">
          <div class="mb-2 flex flex-wrap items-center gap-2">
            <span
              class="text-xs font-semibold uppercase tracking-[0.14em] text-n-teal-11"
            >
              Operação IXC
            </span>
            <span
              v-if="enabled"
              class="inline-flex items-center gap-1.5 rounded-full bg-n-teal-3 px-2.5 py-1 text-xs font-medium text-n-teal-11"
            >
              <span class="size-1.5 rounded-full bg-n-teal-9" />
              Integração ativa
            </span>
          </div>
          <h1 class="mb-1 text-2xl font-semibold text-n-slate-12">
            Indicadores de avisos
          </h1>
          <p class="mb-0 max-w-2xl text-sm leading-6 text-n-slate-11">
            Acompanhe tentativas, aceite pelo WhatsApp e falhas dos avisos de
            boleto enviados a partir do IXC.
          </p>
        </div>
      </div>
      <div v-if="enabled" class="flex flex-col gap-2 sm:flex-row">
        <Button
          label="Enviar lista"
          icon="i-lucide-file-up"
          color="slate"
          variant="outline"
          @click="openCsv"
        />
        <Button
          label="Testar envio"
          icon="i-lucide-send"
          color="teal"
          @click="openTest"
        />
      </div>
    </section>

    <section
      v-if="!enabled"
      class="flex items-start gap-3 rounded-xl border border-n-amber-7 bg-n-amber-3 p-5 text-n-amber-12"
    >
      <i class="i-lucide-unplug mt-0.5 size-5 shrink-0" />
      <div>
        <h2 class="font-semibold">Integração IXC inativa</h2>
        <p class="mb-0 mt-1 text-sm text-n-amber-11">
          Ative a integração ERP com o provedor IXC para acessar os indicadores
          e as ferramentas de envio.
        </p>
      </div>
    </section>

    <template v-else>
      <section class="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <article
          v-for="metric in metricCards"
          :key="metric.key"
          class="rounded-xl border border-n-weak bg-n-card p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <p class="mb-0 text-sm font-medium text-n-slate-11">
              {{ metric.label }}
            </p>
            <span
              class="grid size-8 shrink-0 place-content-center rounded-lg"
              :class="metric.iconClass"
            >
              <i class="size-4" :class="metric.icon" />
            </span>
          </div>
          <p class="mb-0 mt-3 text-2xl font-semibold text-n-slate-12">
            {{ metric.value }}
          </p>
        </article>
      </section>

      <section
        class="grid gap-3 rounded-xl border border-n-weak bg-n-card p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center"
      >
        <div class="flex items-start gap-3">
          <span
            class="grid size-9 shrink-0 place-content-center rounded-lg"
            :class="
              summary.failed
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            <i
              class="size-5"
              :class="
                summary.failed
                  ? 'i-lucide-triangle-alert'
                  : 'i-lucide-shield-check'
              "
            />
          </span>
          <div>
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{
                summary.failed
                  ? `${summary.failed} envio(s) exigem atenção`
                  : 'Nenhuma falha no período'
              }}
            </h2>
            <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
              “Aceito” confirma que o provedor recebeu o disparo. Entrega e
              leitura dependem dos retornos posteriores do WhatsApp.
            </p>
          </div>
        </div>
        <div class="rounded-lg bg-n-alpha-2 px-4 py-2.5 text-right">
          <p class="mb-0 text-xs text-n-slate-10">Taxa de aceite</p>
          <strong class="text-lg text-n-slate-12">{{ acceptedRate }}</strong>
        </div>
      </section>

      <section
        class="overflow-hidden rounded-xl border border-n-weak bg-n-card"
      >
        <header class="border-b border-n-weak p-4 sm:p-5">
          <div
            class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                Histórico de disparos
              </h2>
              <p class="mb-0 mt-1 text-xs text-n-slate-10">
                Atualizado em {{ formatDateTime(lastUpdatedAt) }}
              </p>
            </div>
            <Button
              label="Atualizar"
              icon="i-lucide-refresh-cw"
              color="slate"
              variant="ghost"
              size="sm"
              :is-loading="isLoading"
              @click="fetchReport"
            />
          </div>
          <div class="grid gap-3 md:grid-cols-[1fr_1fr_1.4fr] md:items-end">
            <label class="flex flex-col gap-2 text-sm text-n-slate-12">
              <span>Status</span>
              <select
                v-model="filters.status"
                :class="selectClass"
                @change="fetchReport"
              >
                <option
                  v-for="option in statusOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <label class="flex flex-col gap-2 text-sm text-n-slate-12">
              <span>Modelo do aviso</span>
              <select
                v-model="filters.notification_type"
                :class="selectClass"
                @change="fetchReport"
              >
                <option
                  v-for="option in reportTypeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <div class="flex flex-col gap-2 text-sm text-n-slate-12">
              <span>Período do envio</span>
              <WootDatePicker
                v-model:date-range="dateRange"
                v-model:range-type="rangeType"
                teleport-to-body
                @date-range-changed="onDateRangeChanged"
              />
            </div>
          </div>
        </header>

        <div
          class="hidden grid-cols-[1.2fr_1.3fr_repeat(5,minmax(68px,0.65fr))_48px] gap-3 border-b border-n-weak bg-n-alpha-2 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-n-slate-10 lg:grid"
        >
          <span>Data e hora</span><span>Modelo</span><span>Total</span
          ><span>Aceitos</span><span>Falhas</span><span>Pendentes</span
          ><span>Ignorados</span><span />
        </div>
        <div v-if="isLoading" class="px-4 py-12 text-center text-n-slate-11">
          Carregando indicadores...
        </div>
        <div
          v-else-if="!groupedReportRows.length"
          class="px-4 py-12 text-center text-n-slate-11"
        >
          <i class="i-lucide-inbox mb-3 inline-block size-7 text-n-slate-9" />
          <p class="mb-0">Nenhum envio encontrado para estes filtros.</p>
        </div>
        <div v-else class="divide-y divide-n-weak">
          <article
            v-for="group in groupedReportRows"
            :key="group.key"
            class="grid gap-3 px-5 py-4 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-1 lg:grid-cols-[1.2fr_1.3fr_repeat(5,minmax(68px,0.65fr))_48px] lg:items-center"
          >
            <strong class="font-medium">{{ group.sentDate }}</strong>
            <span class="min-w-0">
              <span class="block">{{ typeLabel(group.notificationType) }}</span>
              <small
                v-if="group.errors.length"
                class="mt-1 block truncate text-xs text-n-ruby-11"
                :title="group.errors.join(' · ')"
              >
                {{ group.errors[0] }}
                <template v-if="group.errors.length > 1">
                  (+{{ group.errors.length - 1 }})
                </template>
              </small>
            </span>
            <span>{{ group.summary.total }}</span>
            <span class="font-medium text-n-teal-11">
              {{ group.summary.sent }}
            </span>
            <span :class="group.summary.failed ? 'text-n-ruby-11' : ''">
              {{ group.summary.failed }}
            </span>
            <span :class="group.summary.pending ? 'text-n-amber-11' : ''">
              {{ group.summary.pending }}
            </span>
            <span>{{ group.summary.skipped }}</span>
            <button
              type="button"
              class="grid size-9 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-11 transition-colors hover:bg-n-alpha-3 hover:text-n-slate-12"
              title="Ver envios"
              @click="openDetails(group)"
            >
              <i class="i-lucide-eye size-4" />
            </button>
          </article>
        </div>
        <div
          v-if="hasMore"
          class="flex items-start gap-2 border-t border-n-amber-7 bg-n-amber-3 px-5 py-3 text-xs text-n-amber-11"
        >
          <i class="i-lucide-info mt-0.5 size-4 shrink-0" />
          O período possui mais de 2.000 registros. Reduza o intervalo para
          consultar todos os detalhes.
        </div>
      </section>
    </template>
  </div>

  <Dialog
    ref="testDialogRef"
    width="2xl"
    title="Testar aviso de boleto"
    description="Valide um dos modelos usando os dados reais de um boleto disponível no IXC."
    confirm-button-label="Enviar teste"
    cancel-button-label="Cancelar"
    :is-loading="isTesting"
    @confirm="submitTest"
  >
    <div class="flex flex-col gap-5">
      <div class="grid gap-4 sm:grid-cols-2">
        <Input
          v-model="testDocument"
          label="CPF do cliente"
          placeholder="000.000.000-00"
          inputmode="numeric"
          autocomplete="off"
          maxlength="14"
          required
        />
        <Input
          v-model="testPhone"
          label="Telefone que receberá o teste"
          placeholder="(00) 00000-0000"
          inputmode="tel"
          autocomplete="tel"
          maxlength="15"
          required
        />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          Modelo do envio
        </span>
        <div class="grid grid-cols-2 gap-2">
          <Button
            v-for="option in typeOptions"
            :key="option.value"
            type="button"
            :label="option.label"
            :icon="option.icon"
            :variant="
              testForm.notification_type === option.value ? 'faded' : 'outline'
            "
            :color="
              testForm.notification_type === option.value ? 'teal' : 'slate'
            "
            class="w-full"
            @click="testForm.notification_type = option.value"
          />
        </div>
      </div>

      <div
        class="flex items-start gap-3 rounded-lg border border-n-teal-7 bg-n-teal-3 p-3 text-sm text-n-teal-12"
      >
        <span class="i-lucide-shield-check mt-0.5 size-5 shrink-0" />
        <span>
          <strong class="block font-medium">
            Envio protegido para o telefone de teste
          </strong>
          <span class="mt-1 block text-xs leading-5 text-n-teal-11">
            O cliente e o boleto serão localizados no IXC, mas o aviso será
            enviado somente ao número informado acima.
          </span>
          <span class="mt-1 block text-xs leading-5 text-n-teal-11">
            {{
              selectedTestConfiguration.template_name ||
              'Template não configurado'
            }}
            ·
            {{
              selectedTestConfiguration.inbox_name || 'Caixa não configurada'
            }}
          </span>
        </span>
      </div>
    </div>
  </Dialog>

  <Dialog
    ref="csvDialogRef"
    width="2xl"
    title="Enviar avisos por lista"
    description="Escolha como o aviso será enviado e informe somente os CPFs dos clientes."
    confirm-button-label="Validar e agendar"
    cancel-button-label="Fechar"
    :is-loading="isImporting"
    overflow-y-auto
    sticky-header-footer
    @confirm="submitCsv"
  >
    <div class="flex flex-col gap-5">
      <div class="grid gap-4 md:grid-cols-2">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span class="font-medium">Caixa do WhatsApp</span>
          <select
            v-model="csvForm.inbox_id"
            :class="selectClass"
            @change="onCsvInboxChange"
          >
            <option disabled value="">Selecione a caixa</option>
            <option
              v-for="inbox in manualInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span class="font-medium">Template do aviso</span>
          <select v-model="csvForm.template_key" :class="selectClass">
            <option disabled value="">Selecione o template</option>
            <option
              v-for="template in csvTemplates"
              :key="templateKey(template)"
              :value="templateKey(template)"
            >
              {{ template.name }} · {{ template.language }}
            </option>
          </select>
        </label>
      </div>

      <Input
        v-model="csvForm.due_date"
        type="date"
        label="Data de vencimento do boleto"
      />

      <div
        class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-3 text-sm text-n-slate-11"
      >
        <span class="i-lucide-list-checks mt-0.5 size-5 shrink-0" />
        <span>
          Informe apenas uma coluna de CPF, com ou sem o cabeçalho
          <code>cpf</code>. Pontos, traços e espaços serão removidos
          automaticamente.
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          Como deseja informar a lista?
        </span>
        <div class="grid grid-cols-2 gap-2">
          <Button
            v-for="option in csvModeOptions"
            :key="option.value"
            type="button"
            :label="option.label"
            :icon="option.icon"
            :variant="csvMode === option.value ? 'faded' : 'outline'"
            :color="csvMode === option.value ? 'teal' : 'slate'"
            class="w-full"
            @click="csvMode = option.value"
          />
        </div>
      </div>

      <TextArea
        v-if="csvMode === 'paste'"
        v-model="csvContent"
        label="Lista de CPFs"
        placeholder="cpf&#10;529.982.247-25&#10;111 444 777-35"
        :max-length="500000"
        auto-height
        resize
        min-height="9rem"
        max-height="18rem"
        custom-text-area-class="font-mono text-xs"
      />

      <div v-else class="flex flex-col gap-2">
        <label
          class="flex min-h-36 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-black2 px-4 py-6 text-center transition-colors hover:bg-n-alpha-2"
        >
          <span class="i-lucide-file-up size-6 text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            Escolher arquivo CSV
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              csvFile?.name || 'Clique para selecionar um arquivo de até 1 MB'
            }}
          </span>
          <input
            class="hidden"
            type="file"
            accept=".csv,text/csv"
            @change="onCsvFile"
          />
        </label>
      </div>

      <p class="mb-0 text-xs leading-5 text-n-slate-11">
        Para cada CPF, o Talkvia localizará no IXC o boleto em aberto com a data
        escolhida antes de agendar o envio.
      </p>

      <div
        v-if="importResult"
        class="rounded-lg border border-n-weak p-4 text-sm"
      >
        <p class="mb-2 font-medium text-n-slate-12">
          {{ importResult.scheduled }} linha(s) agendada(s) ·
          {{ importResult.rejected }} rejeitada(s)
        </p>
        <ul
          v-if="importResult.errors?.length"
          class="max-h-32 list-disc overflow-auto pl-5 text-n-ruby-11"
        >
          <li v-for="item in importResult.errors" :key="item.line">
            Linha {{ item.line }}: {{ item.messages.join(', ') }}
          </li>
        </ul>
      </div>
    </div>
  </Dialog>

  <Dialog
    ref="detailsDialogRef"
    width="5xl"
    :show-confirm-button="false"
    cancel-button-label="Fechar"
  >
    <section v-if="selectedGroup" class="grid gap-4">
      <header>
        <h2 class="text-lg font-semibold text-n-slate-12">Detalhes do envio</h2>
        <p class="text-sm text-n-slate-11">
          {{ selectedGroup.sentDate }} ·
          {{ typeLabel(selectedGroup.notificationType) }}
        </p>
      </header>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div
          v-for="metric in [
            { label: 'Total', value: selectedGroupSummary.total },
            { label: 'Aceitos', value: selectedGroupSummary.sent },
            { label: 'Falhas', value: selectedGroupSummary.failed },
            { label: 'Pendentes', value: selectedGroupSummary.pending },
            { label: 'Ignorados', value: selectedGroupSummary.skipped },
          ]"
          :key="metric.label"
          class="rounded-lg border border-n-weak bg-n-alpha-2 p-3"
        >
          <p class="text-xs text-n-slate-10">{{ metric.label }}</p>
          <strong class="text-xl text-n-slate-12">{{ metric.value }}</strong>
        </div>
      </div>
      <div class="max-h-[55vh] overflow-auto rounded-lg border border-n-weak">
        <table class="min-w-full text-left text-sm">
          <thead class="bg-n-alpha-2 text-n-slate-11">
            <tr>
              <th class="px-3 py-2">Status</th>
              <th class="px-3 py-2">Data e hora</th>
              <th class="px-3 py-2">Cliente</th>
              <th class="px-3 py-2">Telefone</th>
              <th class="px-3 py-2">Vencimento</th>
              <th class="px-3 py-2">Valor</th>
              <th class="px-3 py-2">Template</th>
              <th class="px-3 py-2">Erro</th>
              <th class="px-3 py-2" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-12">
            <tr v-for="row in selectedGroup.rows" :key="row.id">
              <td class="px-3 py-2">
                <span
                  class="inline-flex rounded-full px-2 py-1 text-xs font-medium"
                  :class="statusClasses[row.status] || statusClasses.pending"
                >
                  {{ statusLabels[row.status] || row.status }}
                </span>
              </td>
              <td class="whitespace-nowrap px-3 py-2">
                {{ formatDateTime(deliveryDate(row)) }}
              </td>
              <td class="px-3 py-2">{{ row.customer_name || '-' }}</td>
              <td class="px-3 py-2">{{ row.customer_phone || '-' }}</td>
              <td class="px-3 py-2">{{ formatDate(row.due_date) }}</td>
              <td class="px-3 py-2">{{ formatCurrency(row.amount) }}</td>
              <td class="px-3 py-2">
                {{ row.template_name || '-' }}
              </td>
              <td class="max-w-72 px-3 py-2 text-n-ruby-11">
                <span v-if="row.error_message" :title="row.error_message">
                  {{ row.error_message }}
                </span>
                <span v-else class="text-n-slate-10">-</span>
              </td>
              <td class="px-3 py-2">
                <a
                  v-if="row.conversation_id"
                  :href="conversationHref(row)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-n-brand"
                  >Ver conversa</a
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </Dialog>
</template>
